import { useCallback, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcTradereportingV1CatReportQueryServiceCatReportGet } from '@interxfi/api-client/generated/hooks/ixc-tradereporting-v1-cat-report-query-service-cat-report-get';
import { useIxfReportsV1ReportCommandServiceReportDownload } from '@interxfi/api-client/generated/hooks/ixf-reports-v1-report-command-service-report-download';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import { type IxcTradereportingV1CatReportState, IxcTradereportingV1CatReportStatus } from '@interxfi/types';
import { downloadFromUrl } from '../../../../../../../../../../features/document/utils';
import { CatReportEventDrawer, CatReportEventSummary } from '../../../../../../../../../../features/trade-reporting/cat/cat-report-event';
import { getTradeReportingCatReportStateListItems } from '../../../../../../../../../../features/trade-reporting/cat/cat-report-list-items';
import { getCatReportDetailPath } from '../../../../../../../../../../features/trade-reporting/cat/cat-report-routing';
import { CatReportStatusPill } from '../../../../../../../../../../features/trade-reporting/cat/cat-report-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/reporting/cat/_cat/$reportId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `cat-report-${params.reportId}`,
        label: 'CAT Report',
        to: getCatReportDetailPath(params.reportId),
        suffix: (
          <CopyButton content={params.reportId} label="Copy CAT Report ID" tooltip={{ copy: params.reportId, copied: 'Copied CAT Report ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('CAT Report'),
  component: CatReportDetail,
});

function getListItems(data: IxcTradereportingV1CatReportState | undefined) {
  const listItems = getTradeReportingCatReportStateListItems(data);

  return {
    general: {
      left: [listItems.data.reportDate, listItems.data.mpid, listItems.data.totalEvents, listItems.data.fileKeys] satisfies ReadonlyListItem[],
      right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.reportId] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(
  reportId: string,
  status: IxcTradereportingV1CatReportStatus | undefined,
  downloadReport: VoidFunction,
  isDownloading: boolean,
): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: CatReportEventDrawer,
        props: { request: { reportId } },
      },
    },
    actions:
      status === IxcTradereportingV1CatReportStatus.Generated || status === IxcTradereportingV1CatReportStatus.Submitted
        ? [
            {
              id: 'download',
              icon: <DownloadIcon aria-hidden />,
              label: 'Download',
              disabled: isDownloading,
              onClick: downloadReport,
            },
          ]
        : [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load CAT report', 'view this CAT report');
const downloadErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to download report', 'download this report');

function CatReportDetail() {
  const { reportId } = Route.useParams();
  const request = { reportId } as const;
  const { data, error, isPending } = useIxcTradereportingV1CatReportQueryServiceCatReportGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const { mutateAsync: downloadReport, error: downloadError, isPending: isDownloading } = useIxfReportsV1ReportCommandServiceReportDownload();
  useErrorHandler(downloadError, downloadErrorMessageGetter);
  const handleDownloadReport = useCallback(async () => {
    try {
      const res = await downloadReport({ reportId });

      if (res?.url) {
        downloadFromUrl(res.url);
      }
    } catch {}
  }, [downloadReport, reportId]);

  const listItems = useMemo(() => getListItems(data?.catReport), [data?.catReport]);
  const menuItems = useMemo(
    () => getMenuItems(reportId, data?.catReport?.status, handleDownloadReport, isDownloading),
    [reportId, data?.catReport?.status, handleDownloadReport, isDownloading],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <CatReportStatusPill isPending={showPending} size="large" status={data?.catReport?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <CatReportEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
